// Market session clocks — pure logic, no network, no data licence needed.
//
// Hours are each exchange's published LOCAL trading hours; Intl handles DST.
// Holiday calendars are included only where we can state them with
// confidence (US, UK, Germany, Euronext Paris, 2026–2027). Other exchanges
// are flagged `holidaysKnown: false` and the UI says so — a clock that shows
// "open" on a local holiday is worse than one that admits the gap.
//
// kinds: 'pre' | 'open' | 'post' | 'night' ; gaps between two same-day
// 'open' sessions are reported as a 'break' (lunch).

export const EXCHANGES = [
    {
        id: 'nyse',
        short: 'NYSE',
        name: 'NYSE · Nasdaq',
        city: 'New York',
        tz: 'America/New_York',
        featured: true,
        sessions: [
            ['pre', '04:00', '09:30'],
            ['open', '09:30', '16:00'],
            ['post', '16:00', '20:00'],
        ],
        holidaysKnown: true,
        holidays: [
            '2026-01-01',
            '2026-01-19',
            '2026-02-16',
            '2026-04-03',
            '2026-05-25',
            '2026-06-19',
            '2026-07-03',
            '2026-09-07',
            '2026-11-26',
            '2026-12-25',
            '2027-01-01',
            '2027-01-18',
            '2027-02-15',
            '2027-03-26',
            '2027-05-31',
            '2027-06-18',
            '2027-07-05',
            '2027-09-06',
            '2027-11-25',
            '2027-12-24',
        ],
        // Early close 13:00 ET (after-hours then run to 17:00)
        early: {
            '2026-11-27': ['13:00', '17:00'],
            '2026-12-24': ['13:00', '17:00'],
            '2027-11-26': ['13:00', '17:00'],
        },
    },
    {
        id: 'lse',
        short: 'LSE',
        name: 'London Stock Exchange',
        city: 'London',
        tz: 'Europe/London',
        featured: true,
        sessions: [['open', '08:00', '16:30']],
        holidaysKnown: true,
        holidays: [
            '2026-01-01',
            '2026-04-03',
            '2026-04-06',
            '2026-05-04',
            '2026-05-25',
            '2026-08-31',
            '2026-12-25',
            '2026-12-28',
            '2027-01-01',
            '2027-03-26',
            '2027-03-29',
            '2027-05-03',
            '2027-05-31',
            '2027-08-30',
            '2027-12-27',
            '2027-12-28',
        ],
        early: {
            '2026-12-24': ['12:30'],
            '2026-12-31': ['12:30'],
            '2027-12-24': ['12:30'],
            '2027-12-31': ['12:30'],
        },
    },
    {
        id: 'xetra',
        short: 'XETRA',
        name: 'Frankfurt · Xetra',
        city: 'Frankfurt',
        tz: 'Europe/Berlin',
        featured: true,
        sessions: [['open', '09:00', '17:30']],
        holidaysKnown: true,
        holidays: [
            '2026-01-01',
            '2026-04-03',
            '2026-04-06',
            '2026-05-01',
            '2026-12-24',
            '2026-12-25',
            '2026-12-31',
            '2027-01-01',
            '2027-03-26',
            '2027-03-29',
            '2027-12-24',
            '2027-12-31',
        ],
    },
    {
        id: 'euronext',
        short: 'PARIS',
        name: 'Euronext Paris',
        city: 'Paris',
        tz: 'Europe/Paris',
        sessions: [['open', '09:00', '17:30']],
        holidaysKnown: true,
        holidays: [
            '2026-01-01',
            '2026-04-03',
            '2026-04-06',
            '2026-05-01',
            '2026-12-25',
            '2027-01-01',
            '2027-03-26',
            '2027-03-29',
        ],
        early: {
            '2026-12-24': ['14:05'],
            '2026-12-31': ['14:05'],
            '2027-12-24': ['14:05'],
            '2027-12-31': ['14:05'],
        },
    },
    {
        id: 'tse',
        short: 'TOKYO',
        name: 'Tokyo Stock Exchange',
        city: 'Tokyo',
        tz: 'Asia/Tokyo',
        featured: true,
        // Afternoon session runs to 15:30 since Nov 2024. Night = OSE derivatives (Nikkei 225 futures).
        sessions: [
            ['open', '09:00', '11:30'],
            ['open', '12:30', '15:30'],
            ['night', '16:30', '06:00'],
        ],
        holidaysKnown: false,
    },
    {
        id: 'hkex',
        short: 'HKEX',
        name: 'Hong Kong Exchanges',
        city: 'Hong Kong',
        tz: 'Asia/Hong_Kong',
        // Night = HKFE after-hours futures trading
        sessions: [
            ['pre', '09:00', '09:30'],
            ['open', '09:30', '12:00'],
            ['open', '13:00', '16:00'],
            ['night', '17:15', '03:00'],
        ],
        holidaysKnown: false,
    },
    {
        id: 'sse',
        short: 'SHANGHAI',
        name: 'Shanghai Stock Exchange',
        city: 'Shanghai',
        tz: 'Asia/Shanghai',
        sessions: [
            ['pre', '09:15', '09:30'],
            ['open', '09:30', '11:30'],
            ['open', '13:00', '15:00'],
        ],
        holidaysKnown: false,
    },
    {
        id: 'nse',
        short: 'NSE',
        name: 'NSE India',
        city: 'Mumbai',
        tz: 'Asia/Kolkata',
        sessions: [
            ['pre', '09:00', '09:15'],
            ['open', '09:15', '15:30'],
        ],
        holidaysKnown: false,
    },
    {
        id: 'asx',
        short: 'ASX',
        name: 'ASX Australia',
        city: 'Sydney',
        tz: 'Australia/Sydney',
        sessions: [
            ['pre', '07:00', '10:00'],
            ['open', '10:00', '16:00'],
        ],
        holidaysKnown: false,
    },
    {
        id: 'cme',
        short: 'CME',
        name: 'CME Globex futures',
        city: 'Chicago',
        tz: 'America/Chicago',
        // Sun–Thu 17:00 → next day 16:00 CT (daily 60-min halt). Opens Sunday evening.
        sessions: [['open', '17:00', '16:00']],
        days: [0, 1, 2, 3, 4],
        holidaysKnown: false,
    },
    {
        id: 'crypto',
        short: 'CRYPTO',
        name: 'Crypto markets',
        city: '24/7',
        tz: 'UTC',
        always: true,
        holidaysKnown: true,
    },
];

// ---------- time-zone helpers ----------

const fmtCache = new Map();
function partsIn(tz, epoch) {
    let f = fmtCache.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-GB', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            weekday: 'short',
        });
        fmtCache.set(tz, f);
    }
    const o = {};
    for (const p of f.formatToParts(epoch)) o[p.type] = p.value;
    return { y: +o.year, m: +o.month, d: +o.day, hh: +o.hour, mm: +o.minute, wd: o.weekday };
}

/** Epoch ms of a wall-clock time in `tz` (DST-safe via two-pass offset). */
export function zoned(y, m, d, hh, mm, tz) {
    const guess = Date.UTC(y, m - 1, d, hh, mm);
    const off = (e) => {
        const p = partsIn(tz, e);
        return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm) - Math.floor(e / 60000) * 60000;
    };
    let t = guess - off(guess);
    t = guess - off(t);
    return t;
}

const WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const hm = (s) => s.split(':').map(Number);
const isoDate = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** All session intervals starting on local days (-1 .. +7) around `now`. */
function intervals(ex, now) {
    const out = [];
    const base = partsIn(ex.tz, now);
    for (let off = -1; off <= 7; off++) {
        const dayUTC = new Date(Date.UTC(base.y, base.m - 1, base.d + off));
        const y = dayUTC.getUTCFullYear(),
            m = dayUTC.getUTCMonth() + 1,
            d = dayUTC.getUTCDate();
        const wd = dayUTC.getUTCDay();
        const tradingDays = ex.days || [1, 2, 3, 4, 5];
        if (!tradingDays.includes(wd)) continue;
        const date = isoDate(y, m, d);
        if (ex.holidays?.includes(date)) continue;
        const early = ex.early?.[date];
        let openIdx = 0;
        for (const [kind, s, e] of ex.sessions) {
            let [sh, sm] = hm(s);
            let [eh, em] = hm(e);
            if (early && kind === 'open') [eh, em] = hm(early[0]);
            if (early && kind === 'post') {
                [sh, sm] = hm(early[0]);
                if (early[1]) [eh, em] = hm(early[1]);
            }
            const start = zoned(y, m, d, sh, sm, ex.tz);
            let end = zoned(y, m, d, eh, em, ex.tz);
            if (end <= start) end = zoned(y, m, d + 1, eh, em, ex.tz); // crosses midnight
            if (kind === 'open') openIdx++;
            out.push({ kind, start, end, date });
        }
    }
    return out.sort((a, b) => a.start - b.start);
}

/**
 * Status of one exchange at `now` (epoch ms).
 * → { kind: 'open'|'pre'|'post'|'night'|'break'|'closed'|'holiday',
 *     until: epoch|null, next: { kind, at }|null, localTime: 'HH:MM' }
 */
export function exchangeStatus(ex, now = Date.now()) {
    const p = partsIn(ex.tz, now);
    const localTime = `${String(p.hh).padStart(2, '0')}:${String(p.mm).padStart(2, '0')}`;
    if (ex.always) return { kind: 'open', until: null, next: null, localTime };

    const list = intervals(ex, now);
    const cur = list.find((i) => i.start <= now && now < i.end);
    const upcoming = list.find((i) => i.start > now);
    const next = upcoming ? { kind: upcoming.kind, at: upcoming.start } : null;
    if (cur) return { kind: cur.kind, until: cur.end, next, localTime };

    // Between two regular sessions on the same local day = lunch break
    const prev = [...list].reverse().find((i) => i.end <= now);
    if (
        prev &&
        upcoming &&
        prev.kind === 'open' &&
        upcoming.kind === 'open' &&
        prev.date === upcoming.date
    ) {
        return { kind: 'break', until: upcoming.start, next, localTime };
    }
    const today = isoDate(p.y, p.m, p.d);
    const isHoliday = ex.holidays?.includes(today) && WD[p.wd] >= 1 && WD[p.wd] <= 5;
    return { kind: isHoliday ? 'holiday' : 'closed', until: null, next, localTime };
}

export const KIND_LABEL = {
    open: 'Open',
    pre: 'Pre-market',
    post: 'After-hours',
    night: 'Night session',
    break: 'Lunch break',
    closed: 'Closed',
    holiday: 'Holiday',
};

/** "2h 14m" / "38m" / "3d 4h" */
export function countdown(ms) {
    const mins = Math.max(0, Math.round(ms / 60000));
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    if (d) return `${d}d ${h}h`;
    if (h) return `${h}h ${m}m`;
    return `${m}m`;
}
